import { useEffect, useState } from 'react';
import axios from 'axios';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Building2, TrendingUp, TrendingDown, Minus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const sentimentColors = {
  Positive: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: TrendingUp },
  Neutral: { bg: 'bg-slate-100', text: 'text-slate-700', icon: Minus },
  Negative: { bg: 'bg-red-100', text: 'text-red-700', icon: TrendingDown }
};

export const Competitor = () => {
  const [competitorData, setCompetitorData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/analytics/competitors`);
        setCompetitorData(response.data);
      } catch (error) {
        console.error('Error fetching competitor data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const chartData = [
    { name: 'Your Brand', share: 17, fill: '#8B5CF6' },
    ...competitorData.map((item, index) => ({
      name: item.name,
      share: parseFloat(item.marketShare),
      fill: ['#EC4899', '#3B82F6', '#10B981', '#F59E0B'][index % 4]
    }))
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64" data-testid="loading">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="competitor-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Competitor Analysis</h1>
          <p className="text-slate-500 mt-1">Track and compare your market position against competitors</p>
        </div>

        {/* Market Share Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="bg-white shadow-sm" data-testid="market-share-chart">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-800">Market Share Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                    <XAxis type="number" tickFormatter={(v) => `${v}%`} />
                    <YAxis dataKey="name" type="category" width={100} tick={{ fill: '#64748B', fontSize: 12 }} />
                    <Tooltip 
                      formatter={(value) => [`${value}%`, 'Market Share']}
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #E2E8F0',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar dataKey="share" radius={[0, 4, 4, 0]}>
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Your Position Card */}
          <Card className="bg-white shadow-sm" data-testid="your-position-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-800">Your Market Position</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 rounded-xl">
                  <Building2 className="h-8 w-8 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Current Rank</p>
                  <p className="text-3xl font-bold text-slate-800">#3</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Market Share</p>
                  <p className="text-xl font-bold text-purple-600">17%</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Growth</p>
                  <p className="text-xl font-bold text-emerald-600">+4.2%</p>
                </div>
              </div>
              <p className="text-sm text-slate-600">
                You're outperforming 2 competitors in growth rate. Focus on expanding brand awareness to capture more market share.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Competitor Table */}
        <Card className="bg-white shadow-sm" data-testid="competitor-table">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-800">Competitor Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Competitor</TableHead>
                  <TableHead>Market Share</TableHead>
                  <TableHead>Growth</TableHead>
                  <TableHead>Sentiment</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {competitorData.map((item) => {
                  const sentiment = sentimentColors[item.sentiment];
                  const SentimentIcon = sentiment.icon;
                  return (
                    <TableRow key={item.name} data-testid={`competitor-row-${item.name.toLowerCase().replace(/\s+/g, '-')}`}>
                      <TableCell className="font-medium">{item.name}</TableCell>
                      <TableCell>{item.marketShare}</TableCell>
                      <TableCell>
                        <span className={item.growth.startsWith('+') ? 'text-emerald-600' : 'text-red-600'}>
                          {item.growth}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className={`${sentiment.bg} ${sentiment.text}`}>
                          <SentimentIcon className="h-3 w-3 mr-1" />
                          {item.sentiment}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Competitor;
